import {
  withProps,
  withMethods,
  withComputed,
  signalStoreFeature,
} from '@ngrx/signals';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  UserCredential,
  confirmPasswordReset,
  sendEmailVerification,
  sendPasswordResetEmail,
} from 'firebase/auth';
import {
  Auth,
  User,
  user,
  signOut,
  authState,
  deleteUser,
  applyActionCode,
  checkActionCode,
  signInWithPopup,
  GoogleAuthProvider,
  getAdditionalUserInfo,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
} from '@angular/fire/auth';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { catchError, from, Observable, of, switchMap } from 'rxjs';
import { computed, inject } from '@angular/core';
import {
  authNotApprovedErrorCode,
  authNotApprovedMarker,
} from './utils/auth-error';

type ApprovedAccessState = {
  admin: boolean;
  approved: boolean;
  approvedEmail: string | null;
};

export function withFirebaseAuth() {
  const readApprovedAccess = async (
    firestore: Firestore,
    rawEmail: string | null | undefined,
  ): Promise<ApprovedAccessState> => {
    const approvedEmail = rawEmail?.trim().toLowerCase() || null;
    if (!approvedEmail)
      return { admin: false, approved: false, approvedEmail: null };

    try {
      const approvedUser = await getDoc(
        doc(firestore, 'approvedUsers', approvedEmail),
      );

      const approvedUserData = approvedUser.data();

      if (approvedUserData?.['active'] === true) {
        return {
          admin: approvedUserData['admin'] === true,
          approved: true,
          approvedEmail,
        };
      }
    } catch {
      return { admin: false, approved: false, approvedEmail: null };
    }

    return { admin: false, approved: false, approvedEmail: null };
  };

  return signalStoreFeature(
    withProps(() => {
      const auth = inject(Auth);
      const firestore = inject(Firestore);
      const authUser$ = user(auth);
      const approvedAccess$ = authUser$.pipe(
        switchMap((currentUser) => {
          if (!currentUser)
            return of<ApprovedAccessState>({
              approved: false,
              approvedEmail: null,
              admin: false,
            });

          return from(readApprovedAccess(firestore, currentUser.email)).pipe(
            catchError(() =>
              of<ApprovedAccessState>({
                approved: false,
                approvedEmail: null,
                admin: false,
              }),
            ),
          );
        }),
      );

      return {
        _auth: auth,
        _firestore: firestore,
        _user$: authUser$,
        _getUserSession$: authState(auth),
        _authUser: toSignal(authUser$, { initialValue: null }),
        _approvedAccess: toSignal(approvedAccess$, {
          initialValue: { admin: false, approved: false, approvedEmail: null },
        }),
      };
    }),

    withComputed((store) => ({
      approvedEmail: computed(() => store._approvedAccess().approvedEmail),
      authUser: computed(() =>
        store._approvedAccess().approved ? store._authUser() : null,
      ),
      isAdmin: computed(() => store._approvedAccess().admin),
      isAuthenticated: computed(() => store._approvedAccess().approved),
      authDisplayLabel: computed(() => {
        const currentUser = store._approvedAccess().approved
          ? store._authUser()
          : null;
        if (!currentUser) return '';

        const displayName = currentUser.displayName?.trim();
        if (displayName) return displayName;

        const emailLabel = currentUser.email?.split('@')[0]?.trim();
        if (emailLabel) return emailLabel;

        return 'Cuenta Google';
      }),
    })),

    withMethods((store) => ({
      restoreApprovedSession: async () => {
        const currentUser = store._auth.currentUser;
        if (!currentUser) return false;

        const approvedAccess = await readApprovedAccess(
          store._firestore,
          currentUser.email,
        );

        if (approvedAccess.approved) return true;

        await signOut(store._auth);
        return false;
      },

      signOut: () => from(signOut(store._auth)),

      _deleteCurrentUser: (user: User) => from(deleteUser(user)),

      _verifyEmail: (code: string) => from(applyActionCode(store._auth, code)),

      _sendEmailVerification: (user: User) => from(sendEmailVerification(user)),

      _additionalUserInfo: (user: UserCredential) =>
        getAdditionalUserInfo(user),

      _checkOobCode: (oobCode: string) =>
        from(checkActionCode(store._auth, oobCode)),

      _recoverPassword: (email: string) =>
        from(sendPasswordResetEmail(store._auth, email)),

      _resetPass: (code: string, pass: string) =>
        from(confirmPasswordReset(store._auth, code, pass)),

      _googleSignin: (): Observable<UserCredential> => {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });

        return from(
          signInWithPopup(store._auth, provider)
            .catch(async (error: unknown) => {
              await signOut(store._auth).catch(() => undefined);
              throw error;
            })
            .then(async (credential) => {
              const approvedAccess = await readApprovedAccess(
                store._firestore,
                credential.user.email,
              );

              if (approvedAccess.approved) return credential;

              await signOut(store._auth);
              throw Object.assign(new Error(authNotApprovedMarker), {
                code: authNotApprovedErrorCode,
              });
            }),
        );
      },

      _login: ({
        user,
        pass,
      }: {
        user: string;
        pass: string;
      }): Observable<UserCredential> =>
        from(
          signInWithEmailAndPassword(store._auth, user, pass).then(
            async (credential) => {
              const approvedAccess = await readApprovedAccess(
                store._firestore,
                credential.user.email,
              );

              if (approvedAccess.approved) return credential;

              await signOut(store._auth);
              throw Object.assign(new Error(authNotApprovedMarker), {
                code: authNotApprovedErrorCode,
              });
            },
          ),
        ),

      _createAccount: ({
        user,
        pass,
      }: {
        user: string;
        pass: string;
      }): Observable<UserCredential> =>
        from(createUserWithEmailAndPassword(store._auth, user, pass)),
    })),
  );
}
